"use client";

import { c as _c } from "react/compiler-runtime";
import { workflowsKeys } from "@/app/(v2)/workflows/_hooks/workflowsQueryKeys";
import { ManageAccountModalHost } from "@/components/account/ManageAccountModal";
import { ChainRegistryProvider } from "@/components/providers/ChainRegistryProvider";
import { FormoAnalyticsProvider, FormoIdentify } from "@/components/providers/FormoProvider";
import { GuideProvider } from "@/components/providers/GuideProvider";
import { AnimatedCheckmark } from "@/components/ui/animated-checkmark";
import { OrganizationWalletDetailsModalHost } from "@/components/ui/OrganizationWalletDetailsModalHost";
import { TooltipProvider } from "@/components/ui/tooltip";
import { useAppAuth } from "@/hooks/useAppAuth";
import { organizationKeys, useOrganizations } from "@/hooks/useOrganizations";
import { clearClientSessionCookies, getSelectedOrgId, setSelectedOrgId } from "@/lib/authenticatedFetch";
import { getQueryClient } from "@/lib/queryClient";
import { WORKFLOW_CATALOG_SYNC_EVENT } from "@/lib/workflow/catalogSync";
import { AnyspendProvider } from "@b3dotfun/sdk/anyspend/react";
import { B3DynamicModal, B3Provider } from "@b3dotfun/sdk/global-account/react";
import { ChakraProvider, ColorModeScript, useColorMode } from "@chakra-ui/react";
import { NeynarContextProvider, Theme } from "@neynar/react";
import { QueryClientProvider, useQueryClient } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { usePathname } from "next/navigation";
import { IconAlertWarningOutline18, IconCircleInfoOutline18, IconCircleXmarkOutline18 } from "nucleo-ui-outline-18";
import { useCallback, useEffect, useRef } from "react";
import { Toaster as SonnerToaster } from "sonner";
import theme from "./theme";

// Formo analytics write key
const FORMO_WRITE_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJvcmlnaW4iOiJodHRwczovL2Izb3Mub3JnIiwicHJvamVjdF9pZCI6InpIYVBMSnQ1ZnVDMThuS0ZZZ00wVCIsImlhdCI6MTc3Njk2MDIzOX0.7xnRtTeJewW_aRTDaZVN49hjAoi-cqaH9NzuX2NZdf4";
export function Providers(t0) {
  const $ = _c(7);
  const {
    children
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = getQueryClient();
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const queryClient = t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <ColorModeScript initialColorMode={theme.config.initialColorMode} />;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== children) {
    t3 = <FormoAnalyticsProvider writeKey={FORMO_WRITE_KEY}>{t2}<ChakraProvider theme={theme}><B3ProviderWrapper>{children}</B3ProviderWrapper></ChakraProvider></FormoAnalyticsProvider>;
    $[2] = children;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <ReactQueryDevtools initialIsOpen={false} />;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== t3) {
    t5 = <QueryClientProvider client={queryClient}><ChainRegistryProvider>{t3}{t4}</ChainRegistryProvider></QueryClientProvider>;
    $[5] = t3;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  return t5;
}
function B3ProviderWrapper(t0) {
  const $ = _c(28);
  const {
    children
  } = t0;
  const {
    colorMode
  } = useColorMode();
  const pathname = usePathname();
  const isWorkflowEditor = pathname === "/workflows/edit";
  const queryClient = useQueryClient();
  let t1;
  if ($[0] !== queryClient) {
    t1 = async () => {
      queryClient.invalidateQueries({
        queryKey: organizationKeys.selected()
      });
    };
    $[0] = queryClient;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const onConnect = t1;
  const neynarClientId = process.env.NEXT_PUBLIC_NEYNAR_CLIENT_ID;
  if (!neynarClientId) {
    console.warn("NEXT_PUBLIC_NEYNAR_CLIENT_ID is not set. Farcaster features will be disabled.");
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      showPoints: true
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const t3 = colorMode === "dark" ? Theme.Dark : Theme.Light;
  let t4;
  if ($[3] !== t3) {
    t4 = {
      clientId: neynarClientId || "",
      defaultTheme: t3
    };
    $[3] = t3;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t10;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <FormoIdentify />;
    t6 = <WorkflowCatalogSyncBridge />;
    t7 = <AutoSelectOrgBridge />;
    t8 = <B3DynamicModal />;
    t9 = <ManageAccountModalHost />;
    t10 = <OrganizationWalletDetailsModalHost />;
    $[5] = t10;
    $[6] = t5;
    $[7] = t6;
    $[8] = t7;
    $[9] = t8;
    $[10] = t9;
  } else {
    t10 = $[5];
    t5 = $[6];
    t6 = $[7];
    t7 = $[8];
    t8 = $[9];
    t9 = $[10];
  }
  const t11 = isWorkflowEditor ? "bottom-center" : "bottom-right";
  let t12;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = {
      duration: 4000
    };
    $[11] = t12;
  } else {
    t12 = $[11];
  }
  let t13;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = {
      "--width": "440px"
    };
    $[12] = t13;
  } else {
    t13 = $[12];
  }
  let t14;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = {
      success: <AnimatedCheckmark className="h-4 w-4" strokeWidth={3} />,
      error: <IconCircleXmarkOutline18 />,
      info: <IconCircleInfoOutline18 />,
      warning: <IconAlertWarningOutline18 />
    };
    $[13] = t14;
  } else {
    t14 = $[13];
  }
  let t15;
  if ($[14] !== colorMode || $[15] !== t11) {
    t15 = <SonnerToaster position={t11} theme={colorMode} closeButton={true} toastOptions={t12} offset={16} gap={8} style={t13 as React.CSSProperties} icons={t14} />;
    $[14] = colorMode;
    $[15] = t11;
    $[16] = t15;
  } else {
    t15 = $[16];
  }
  let t16;
  if ($[17] !== children || $[18] !== t15) {
    t16 = <TooltipProvider><GuideProvider>{t5}{t6}{t7}{t8}{t9}{t10}{children}{t15}</GuideProvider></TooltipProvider>;
    $[17] = children;
    $[18] = t15;
    $[19] = t16;
  } else {
    t16 = $[19];
  }
  let t17;
  if ($[20] !== t16 || $[21] !== t4) {
    t17 = <AnyspendProvider featureFlags={t2}><NeynarContextProvider settings={t4}>{t16}</NeynarContextProvider></AnyspendProvider>;
    $[20] = t16;
    $[21] = t4;
    $[22] = t17;
  } else {
    t17 = $[22];
  }
  let t18;
  if ($[23] !== colorMode || $[24] !== onConnect || $[25] !== queryClient || $[26] !== t17) {
    t18 = <B3Provider queryClient={queryClient} environment={process.env.NEXT_PUBLIC_ENV === "development" ? "development" : "production"} theme={colorMode} authStrategy="better-auth" automaticallySetFirstEoa={true} simDuneApiKey={process.env.NEXT_PUBLIC_SIM_DUNE_API_KEY} partnerId={String(process.env.NEXT_PUBLIC_GLOBAL_ACCOUNTS_PARTNER_ID)} onConnect={onConnect} onLogout={_temp} accountOverride={undefined} defaultEoaProvider={undefined} toaster={undefined} rpcUrls={undefined} connectors={undefined} createClientReferenceId={undefined} defaultPermissions={undefined} apiUrl={undefined} notificationsApiUrl={undefined} stripePublishableKey={undefined}>{t17}</B3Provider>;
    $[23] = colorMode;
    $[24] = onConnect;
    $[25] = queryClient;
    $[26] = t17;
    $[27] = t18;
  } else {
    t18 = $[27];
  }
  return t18;
}

/**
 * Triggers auto-select of the user's organization once useOrganizations()
 * has loaded data. This replaces the previous fetchAndStoreOrgOnConnect
 * callback that issued its own parallel /v1/organizations request — that
 * second request did not always share an in-flight promise with the hook's
 * useQuery under React 19 concurrent rendering, leading to a duplicate fetch.
 *
 * The component subscribes via useOrganizations() so it shares the single
 * query observer, and runs the auto-select side effect exactly once per
 * authenticated session.
 */
function _temp() {
  clearClientSessionCookies();
}
function AutoSelectOrgBridge() {
  const $ = _c(11);
  const {
    isAuthenticated
  } = useAppAuth();
  const {
    organizations,
    loading
  } = useOrganizations();
  const queryClient = useQueryClient();
  const orgCount = organizations.length;
  const soleOrgId = orgCount === 1 ? organizations[0].id : null;
  let t0;
  if ($[0] !== isAuthenticated || $[1] !== loading || $[2] !== queryClient || $[3] !== soleOrgId) {
    t0 = () => {
      if (!isAuthenticated || loading) {
        return;
      }
      if (getSelectedOrgId()) {
        return;
      }
      if (soleOrgId) {
        setSelectedOrgId(soleOrgId);
        queryClient.invalidateQueries({
          queryKey: organizationKeys.selected()
        });
      }
    };
    $[0] = isAuthenticated;
    $[1] = loading;
    $[2] = queryClient;
    $[3] = soleOrgId;
    $[4] = t0;
  } else {
    t0 = $[4];
  }
  let t1;
  if ($[5] !== isAuthenticated || $[6] !== loading || $[7] !== orgCount || $[8] !== queryClient || $[9] !== soleOrgId) {
    t1 = [isAuthenticated, loading, orgCount, soleOrgId, queryClient];
    $[5] = isAuthenticated;
    $[6] = loading;
    $[7] = orgCount;
    $[8] = queryClient;
    $[9] = soleOrgId;
    $[10] = t1;
  } else {
    t1 = $[10];
  }
  useEffect(t0, t1);
  return null;
}
function WorkflowCatalogSyncBridge() {
  const $ = _c(4);
  const {
    isAuthenticated
  } = useAppAuth();
  const {
    selectedOrgId
  } = useOrganizations();
  const lastSyncAtRef = useRef(0);
  let t0;
  let t1;
  if ($[0] !== isAuthenticated || $[1] !== selectedOrgId) {
    t0 = () => {
      if (!isAuthenticated || !selectedOrgId || typeof window === "undefined") {
        return;
      }
      const handleSync = () => {
        const now = Date.now();
        if (now - lastSyncAtRef.current < 250) {
          return;
        }
        lastSyncAtRef.current = now;
        getQueryClient().invalidateQueries({
          queryKey: workflowsKeys.all
        });
      };
      const channel = typeof BroadcastChannel === "undefined" ? null : new BroadcastChannel(WORKFLOW_CATALOG_SYNC_EVENT);
      window.addEventListener(WORKFLOW_CATALOG_SYNC_EVENT, handleSync);
      channel?.addEventListener("message", handleSync);
      return () => {
        window.removeEventListener(WORKFLOW_CATALOG_SYNC_EVENT, handleSync);
        channel?.removeEventListener("message", handleSync);
        channel?.close();
      };
    };
    t1 = [isAuthenticated, selectedOrgId];
    $[0] = isAuthenticated;
    $[1] = selectedOrgId;
    $[2] = t0;
    $[3] = t1;
  } else {
    t0 = $[2];
    t1 = $[3];
  }
  useEffect(t0, t1);
  return null;
}